import { get, set } from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from "react";
import { ValidationError } from "yup";
import {
  normalizeFieldPath,
  orderPathsByDomPosition,
  pickFirstErrorPath,
} from "utils/helpers/formErrors";

const FieldRegistryContext = createContext(null);

const FOCUSABLE_SELECTOR = [
  "input:not([disabled])",
  "textarea:not([disabled])",
  "select:not([disabled])",
  "button[role='switch']:not([disabled])",
  "[tabindex]:not([tabindex='-1']):not([disabled])",
].join(", ");

/**
 * External store that tracks mounted fields and their per-path errors.
 * Registration never triggers React re-renders of the provider; each field
 * subscribes to its own path via useSyncExternalStore.
 */
export const createFieldStore = () => {
  const fields = new Map();
  const errors = new Map();
  const listeners = new Map();
  const globalListeners = new Set();

  const notify = (path) => {
    const pathListeners = listeners.get(path);
    if (pathListeners) {
      pathListeners.forEach((listener) => listener());
    }
    globalListeners.forEach((listener) => listener());
  };

  return {
    register(path, meta = {}) {
      if (!path) {
        return;
      }
      const previous = fields.get(path) || {};
      fields.set(path, {
        ...previous,
        ...meta,
        path,
      });
    },

    update(path, meta = {}) {
      if (!path || !fields.has(path)) {
        return;
      }
      fields.set(path, {
        ...fields.get(path),
        ...meta,
        path,
      });
    },

    unregister(path) {
      if (!path) {
        return;
      }
      fields.delete(path);
      if (errors.has(path)) {
        errors.delete(path);
        notify(path);
      }
    },

    setError(path, message) {
      if (!path) {
        return;
      }
      const previous = errors.get(path) || null;
      const next = message || null;
      if (previous === next) {
        return;
      }
      if (next) {
        errors.set(path, next);
      } else {
        errors.delete(path);
      }
      notify(path);
    },

    setErrors(errorMap = {}) {
      const nextPaths = new Set(Object.keys(errorMap));
      const previousPaths = Array.from(errors.keys());

      previousPaths.forEach((path) => {
        if (!nextPaths.has(path)) {
          errors.delete(path);
          notify(path);
        }
      });

      Object.entries(errorMap).forEach(([path, message]) => {
        const previous = errors.get(path) || null;
        if (previous !== message) {
          errors.set(path, message);
          notify(path);
        }
      });
    },

    clearErrors() {
      const previousPaths = Array.from(errors.keys());
      errors.clear();
      previousPaths.forEach((path) => notify(path));
    },

    getError(path) {
      return errors.get(path) || null;
    },

    getErrorMap() {
      return Object.fromEntries(errors.entries());
    },

    getField(path) {
      return fields.get(path) || null;
    },

    getRegisteredFields() {
      return Array.from(fields.values());
    },

    getRegisteredPaths() {
      return Array.from(fields.keys());
    },

    getElement(path) {
      return fields.get(path)?.element || null;
    },

    focusField(path) {
      const field = fields.get(path);
      if (!field || field.disabled) {
        return;
      }
      if (typeof field.focusField === "function") {
        field.focusField();
        return;
      }
      const element = field.element;
      if (!element) {
        return;
      }
      const focusable = element.querySelector(FOCUSABLE_SELECTOR);
      focusable?.focus?.({ preventScroll: true });
    },

    subscribe(path, listener) {
      if (!listeners.has(path)) {
        listeners.set(path, new Set());
      }
      listeners.get(path).add(listener);
      return () => {
        const pathListeners = listeners.get(path);
        if (!pathListeners) {
          return;
        }
        pathListeners.delete(listener);
        if (pathListeners.size === 0) {
          listeners.delete(path);
        }
      };
    },

    subscribeAll(listener) {
      globalListeners.add(listener);
      return () => {
        globalListeners.delete(listener);
      };
    },
  };
};

export const FieldRegistryProvider = ({ store, children }) => (
  <FieldRegistryContext.Provider value={store}>
    {children}
  </FieldRegistryContext.Provider>
);

export const useFieldRegistry = () => {
  const store = useContext(FieldRegistryContext);
  if (!store) {
    throw new Error(
      "useFieldRegistry must be used within a FieldRegistryProvider",
    );
  }
  return store;
};

export const useFieldError = (path) => {
  const store = useFieldRegistry();
  return useSyncExternalStore(
    (listener) => store.subscribe(path, listener),
    () => store.getError(path),
    () => null,
  );
};

/**
 * Binder factory for uncontrolled Additional Information forms.
 * Typing still mutates `values` in place (no React re-render); blur validates
 * a single path and writes the error into the store so only that field
 * re-renders via useFieldError / FormField.
 */
export const useFieldBinder = ({ values, getSchema, onAfterBlur }) => {
  const store = useFieldRegistry();

  const validatePath = useCallback(
    async (path) => {
      // Required/empty is never a field error — it only drives progress %.
      // Blur validates format / business rules when a value is present.
      const schema = typeof getSchema === "function" ? getSchema() : getSchema;
      if (schema?.validateAt) {
        try {
          await schema.validateAt(path, values);
          store.setError(path, null);
          return null;
        } catch (error) {
          // Paths absent from the Yup schema (optional UI-only fields) must
          // not surface as errors — only real ValidationError counts.
          if (ValidationError.isError(error)) {
            const message = error.message || null;
            store.setError(path, message);
            return message;
          }
          store.setError(path, null);
          return null;
        }
      }

      store.setError(path, null);
      return null;
    },
    [getSchema, store, values],
  );

  const bindField = useCallback(
    (
      path,
      {
        label,
        required = false,
        disabled = false,
        extractValue = (eventOrValue) => {
          if (
            eventOrValue &&
            typeof eventOrValue === "object" &&
            "target" in eventOrValue
          ) {
            return eventOrValue.target.value;
          }
          return eventOrValue;
        },
        onChange: onChangeExtra,
        onBlur: onBlurExtra,
      } = {},
    ) => {
      const defaultValue = get(values, path);

      return {
        path,
        name: path,
        id: path,
        label,
        required,
        disabled,
        defaultValue,
        onChange: (eventOrValue) => {
          const nextValue = extractValue(eventOrValue);
          set(values, path, nextValue);
          if (store.getError(path)) {
            validatePath(path);
          }
          if (typeof onChangeExtra === "function") {
            onChangeExtra(nextValue, eventOrValue);
          }
        },
        onBlur: async () => {
          await validatePath(path);
          if (typeof onBlurExtra === "function") {
            onBlurExtra();
          }
          if (typeof onAfterBlur === "function") {
            onAfterBlur(path);
          }
        },
      };
    },
    [onAfterBlur, store, validatePath, values],
  );

  const collectSubmitErrors = useCallback(
    async (submitValues = values) => {
      // Save is blocked only by format / business Yup rules — never by empty
      // required fields. Obligation stays in the progress registry only.
      let yupErrors = {};
      const schema = typeof getSchema === "function" ? getSchema() : getSchema;
      if (schema?.validate) {
        try {
          await schema.validate(submitValues, { abortEarly: false });
        } catch (error) {
          yupErrors = (error.inner || []).reduce((acc, { path, message }) => {
            const normalizedPath = normalizeFieldPath(path);
            if (normalizedPath) {
              acc[normalizedPath] = message;
            }
            return acc;
          }, {});
        }
      }

      // Only keep Yup errors for paths that are currently mounted, so hidden
      // conditional fields cannot block Save.
      const registeredPaths = new Set(store.getRegisteredPaths());
      return Object.fromEntries(
        Object.entries(yupErrors).filter(([path]) => registeredPaths.has(path)),
      );
    },
    [getSchema, store, values],
  );

  const scrollToFirstError = useCallback(
    (errorMap) => {
      const orderedPaths = orderPathsByDomPosition(
        Object.keys(errorMap),
        (path) => store.getElement(path),
      );
      const firstPath = pickFirstErrorPath(errorMap, orderedPaths);
      if (!firstPath) {
        return null;
      }

      const element = store.getElement(firstPath);
      element?.scrollIntoView?.({ behavior: "smooth", block: "start" });
      store.focusField(firstPath);
      return firstPath;
    },
    [store],
  );

  return useMemo(
    () => ({
      bindField,
      validatePath,
      collectSubmitErrors,
      scrollToFirstError,
      store,
    }),
    [bindField, collectSubmitErrors, scrollToFirstError, store, validatePath],
  );
};

export { FOCUSABLE_SELECTOR };
